import { useEffect, useState } from "react";
import { useHeaderContext } from "../context/headerContext";
import { useUserContext } from "../context/userContext";
import PersonCard from "../components/PersonCard";
import Button from "../components/Button";
import SearchIcon from '/public/images/icons/search.svg?react';
import TextInput from "../components/TextInput";
import DatePicker from "../components/DatePicker";
import { formatDistanceToNow } from 'date-fns';
import fetchWithRefresh from "../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../utils/LogErrors";

export default function Log() {
  const { setTitle, setBackButton, setSearchFunc } = useHeaderContext();
  const [filterOverlayVisible, setFilterOverlayVisible] = useState(false);
  const { user, selectedVenue, logout, updateTokens } = useUserContext();
  const [selectedFilter, setSelectedFilter] = useState('All Activity');
  const [searchTerm, setSearchTerm] = useState("");
  const [scanActivity, setScanActivity] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState(null);
  const [filters, setFilters] = useState(['All Activity', 'Closed Emergency', 'Active Emergency']);
  const [scanEvents, setScanEvents] = useState([]);
  const [lastUpdated, setLastUpdated] = useState('');
  const [filterActive, setFilterActive] = useState(false);

  useEffect(() => {
    setTitle("Log");
    setBackButton(true);
    setSearchFunc(toggleFilterOverlay);
  }, [filterOverlayVisible]);

  useEffect(() => {
    fetchScanActivityForVenue();
  }, [selectedVenue]);

  // useEffect(() => {
  //   fetchScanEventsForParticipants();
  // }, [scanActivity]);

  useEffect(() => {
    if (scanActivity.length > 0) {
      const mostRecentEvent = scanActivity.reduce((latest, event) => {
        const eventDate = new Date(event.WhenHappenedDate);
        return eventDate > new Date(latest.WhenHappenedDate) ? event : latest;
      }, scanActivity[0]);
  
      let timeDifference = formatDistanceToNow(new Date(mostRecentEvent.WhenHappenedDate), { addSuffix: true });
      timeDifference = timeDifference.replace(/about\s/, ''); // Remove the word "about"
      setLastUpdated(timeDifference);
    }
  }, [scanActivity]);

  const toggleFilterOverlay = () => {
    setFilterOverlayVisible((prevState) => !prevState);
  };

  const fetchScanActivityForVenue = async () => {
    Sentry.startSpan({ name: "Fetch Scan Activity", op: "scan" }, async (span) => {
      try {
        if (!selectedVenue?.OrgID) {
          Sentry.captureMessage("Scan activity fetch failed: Missing OrgID", "warning");
          console.warn("Missing OrgID for fetching scan activity.");
          return;
        }
  
        const response = await fetchWithRefresh(
          `${import.meta.env.VITE_API_URL}Scan/ScanActivityForVenue?OrgId=${selectedVenue?.OrgID}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          }, 
          logout, 
          updateTokens, 
          (message) => { toast.info(message); }
        );
  
        if (!response.ok) {
          const errorMsg = `Failed to fetch scan activity: ${response.status} - ${response.statusText}`;
          Sentry.captureMessage(errorMsg, "error");
          LogErrors(user, logout, updateTokens, errorMsg);
          toast.error("Error fetching scan activity.");
          return;
        }
  
        const data = await response.json();
        setScanActivity(Array.isArray(data) ? data : [data]);
        span.setStatus("ok");
  
      } catch (error) {
        LogErrors(user, logout, updateTokens, "Error fetching scan activity data: " + error);
        Sentry.captureException(error);
        toast.error("An error occurred while fetching scan activity.");
      } finally {
        span.end();
      }
    });
  };

  const handleFilterClick = (filter) => {
    setSelectedFilter(filter);
  };

  const handleDateFromChange = (e) => {
    setDateFrom(e.target.value);
  };
  
  const handleDateToChange = (e) => {
    setDateTo(e.target.value);
  };

  const filterEvents = () => {
    setFilterActive(false);

    const parseDate = (dateStr) => {
      if (typeof dateStr !== 'string') {
        return null;
      }
      const [day, month, year] = dateStr.split('/');
      return new Date(`${year}-${month}-${day}`);
    };
  
    let filteredData = scanActivity;
    // Apply search term filter
    if (searchTerm) {
      setFilterActive(true);
      const lowercasedFilter = searchTerm.toLowerCase();
      filteredData = filteredData.filter(event =>
        (event.ZoneName && event.ZoneName.toLowerCase().includes(lowercasedFilter)) ||
        (event.ParticipantName && event.ParticipantName.toLowerCase().includes(lowercasedFilter))
      );
    }
  
    // // Apply date range filter
    // const fromDate = dateFrom && typeof dateFrom === 'string' ? parseDate(dateFrom) : null;
    // const toDate = dateTo && typeof dateTo === 'string' ? parseDate(dateTo) : null;
    // if (fromDate || toDate) {
    //   setFilterActive(true);
    //   filteredData = filteredData.filter(event => {
    //     const eventDate = new Date(event.WhenHappened.split(' ')[0]);
    //     return (!fromDate || eventDate >= fromDate) && (!toDate || eventDate <= new Date(toDate.getTime() + 24 * 60 * 60 * 1000 - 1));
    //   });
    // }
  
    // // Apply event type filter
    if (selectedFilter === 'Closed Emergency' || selectedFilter === 'Active Emergency') {
      setFilterActive(true);
      const eventTypeFilter = selectedFilter === 'Closed Emergency' ? 'EMERGENCYCLOSED' : 'EMERGENCY';
      filteredData = filteredData.filter(event => event.EventType === eventTypeFilter);
    }
  
    // Sort events by date
    filteredData.sort((a, b) => new Date(b.WhenHappened) - new Date(a.WhenHappened));
  
    // Remove duplicate events
    const uniqueEvents = [];
    const seenEvents = new Set();
    for (const event of filteredData) {
      const eventKey = `${event.EventId}_${event.WhenHappened}_${event.ZoneName}`;
      if (!seenEvents.has(eventKey)) {
        uniqueEvents.push(event);
        seenEvents.add(eventKey);
      }
    }
  
    // Group events by date
    const groupedEvents = filteredData.reduce((acc, event) => {
      if (!event || !event.WhenHappenedDate) {
        return acc;
      }
      const eventDateTime = event.WhenHappenedDate.split(' ')[0]; // Extract date part
      const [year, month, day] = eventDateTime.split('-');
      const formattedDate = `${day}/${month}/${year}`; // Format to dd/mm/yyyy
      if (!acc[formattedDate]) {
        acc[formattedDate] = [];
      }
      acc[formattedDate].push(event);
      return acc;
    }, {});
  
    // Include cards with matching events
    // const finalGroupedEvents = {};
    // for (const date in filteredData) {
    //   const events = filteredData[date];
    //   const hasMatchingEvent = events.some(event => {
    //     if (selectedFilter === 'Closed Emergency') {
    //       return event.EventType === 'EMERGENCYCLOSED';
    //     } else if (selectedFilter === 'Active Emergency') {
    //       return event.EventType === 'EMERGENCY';
    //     }
    //     return true;
    //   });
    //   if (hasMatchingEvent) {
    //     finalGroupedEvents[date] = events;
    //   }
    // }

    setFilteredEvents(groupedEvents);
  };
  
  useEffect(() => {
    filterEvents();
  }, [searchTerm, selectedFilter, dateFrom, dateTo, scanActivity]);

  const clearFilters = () => {
    setFilterOverlayVisible(false);
    setDateFrom("");
    setDateTo(null);
    setSearchTerm("");
    setSelectedFilter('All Activity');
  }
  
  return (
    <div className="relative bg-sportsLightGrey min-h-screen p-[24px] mb-[60px] flex flex-col gap-2">
      <p className="font-semibold tracking-widest text-[#101820] text-[12px] uppercase">
        {/* LAST UPDATED • {lastUpdated} */}
        {selectedVenue?.OrgPartnerFriendlyName}
      </p>
      <p className={`font-semibold text-[32px] mb-[40px]`}>Today&apos;s scan activity</p>
  
      <div className="flex flex-col">
      {Object.keys(filteredEvents).length > 0 ? (
  Object.keys(filteredEvents).map((date, index) => {
    const cards = filteredEvents[date];
    return (
      <div key={index} className="mb-[12px]">
        <p className="text-[12px] font-medium mb-[12px]">{date}</p>

        <div className="flex flex-col gap-3">
          {cards?.map((card, index) => (
            <PersonCard key={index} userData={card} checkinData={true} />
          ))}
        </div>
      </div>
    );
  })
) : (
  <h4 className="text-[#909090]">
  {filterActive 
    ? "There currently aren’t any scans that match the selected search criteria." 
    : "There currently aren’t any scans today."
  }
</h4>

)}
      </div>

      {filterOverlayVisible && (
        <div className="absolute top-0 left-0 h-screen bg-white p-6 z-[8] max-w-[430px] w-full">
          <div className="flex flex-col gap-x-4">
            <div className="relative w-full">
              <TextInput
                name="search"
                type="text"
                label="Search"
                className="w-full [&>.relative>input]:pr-[42px] [&>.label]:p-0 [&>.label]:pb-1"
                autoComplete="off"
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
                value={searchTerm}
              />
              <SearchIcon className="absolute w-6 h-6 stroke-black right-[14px] bottom-[14px]" />
            </div>
          </div>
          <div className="flex flex-col border-t border-dashed border-[#E3E3E3] pt-10 !mt-12" />
          <div className="flex flex-col gap-x-4">
            <p className="text-sportsBlack font-medium text-base leading-[26px] mb-1">Filters</p>
            <div className="flex flex-row gap-4 flex-wrap">
              {filters.map((filter, index) => (
                <div
                  key={index}
                  className={`flex bg-sportsLightGrey p-2 text-[10px] rounded-full font-medium cursor-pointer border-[2px] border-transparent ${
                    selectedFilter === filter ? "!border-sportsBlue" : ""
                  }`}
                  onClick={() => handleFilterClick(filter)}
                >
                  <p>{filter}</p>
                </div>
              ))}
              {/* <div className="flex w-full justify-between mt-4 max-w-[100%]">
                <div>
                  <DatePicker
                    name="dateFrom"
                    label="From Date"
                    defaultValue={dateFrom || ''}
                    onChange={handleDateFromChange}
                  />
                </div>
                <div>
                  <DatePicker
                    name="dateTo"
                    label="To Date"
                    defaultValue={dateTo || ''}
                    onChange={handleDateToChange}
                  />
                </div>
              </div> */}
            </div>
          </div>
          <div className="!mt-8 flex flex-col gap-4">
            <Button
              variant="primary"
              className="w-full mt-4"
              onClick={() => {
                setFilterOverlayVisible(false);
                filterEvents();
              }}
            >
                Update Search
            </Button>
            {filterActive && (
              <Button variant="quaternary" className={"mb-[40px]"} onClick={() => clearFilters()}>Clear Filters</Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}